import { freshIntent } from "../hooks/cartPersistence";
import { findCartLineIndex } from "./cartLineIdentity";
import { generateCode } from "@delibrandev/utils";
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  corporationData: {
    setCorporationData: {},
    info: {},
    products: [],
    categories: [],
  },
  userConfig: {
    loading: false,
    logged: false,
    cartDiscountBusy: false,
    sessionReady: false,
    logoutPending: false,
    userData: {},
    language: "es",
    literals: [],
    rol: null,
    token: null,
    componentActive: "",
    datatable: {
      configuration: { maxRows: 12, indexStart: 0, pages: [0], filterBy: { section: "", filter: [] } },
      requestQuery: {},
      tables: {},
    },
    alerts: [],
    floatPanel: {
      visible: false,
      queryId: null,
      isEmpty: true,
      data: {},
    },
    order: {},
    historyOrder: {},
    cart: {
      ShoppingCartProducts: [],
    },
    search: "",
    payment: {},
    checkout: { address: null, paymentMethod: null, deliveryMethod: null },
  },
};

const userConfigSlice = createSlice({
  name: "aplicationConfig",
  initialState,
  reducers: {
    updateHistoryOrder: (state, action) => {
      state.userConfig.historyOrder = action.payload;
    },
    updateChekoutAddress: (state, action) => {
      state.userConfig.checkout.address = action.payload;
    },
    updateChekoutPaymentMethod: (state, action) => {
      state.userConfig.checkout.paymentMethod = action.payload;
    },
    updateChekoutDeliveryMethod: (state, action) => {
      state.userConfig.checkout.deliveryMethod = action.payload;
    },
    updateShopProducts: (state, action) => {
      state.corporationData.products = action.payload;
    },
    updateShopCategories: (state, action) => {
      state.corporationData.categories = action.payload;
    },
    setCorporationData: (state, action) => {
      state.corporationData.info = action.payload;
    },
    setLoading: (state, action) => {
      state.userConfig.loading = action.payload;
    },
    setAlert: (state, action) => {
      const id = new Date().getTime(); // ID único basado en el tiempo
      state.userConfig.alerts.push({ id, ...action.payload });
    },
    removeAlert: (state, action) => {
      state.userConfig.alerts = state.userConfig.alerts.filter((alert) => alert.id !== action.payload);
    },
    setSessionReady: (state, action) => {
      state.userConfig.sessionReady = action.payload;
    },
    setUserLogged: (state, action) => {
      state.userConfig.logged = action.payload;
    },
    setUserData: (state, action) => {
      state.userConfig.userData = action.payload;
    },
    setLogoutPending: (state, action) => {
      state.userConfig.logoutPending = action.payload;
    },
    setUserLogOut: (state) => {
      // Clear customer-owned state; preserve the public tenant catalog.
      Object.assign(state.userConfig, {
        logged: false, cartDiscountBusy: false, token: null, rol: null, userData: {},
        sessionReady: true, loading: false, order: {}, historyOrder: {},
        cart: { ShoppingCartProducts: [] }, payment: {},
        checkout: { address: null, paymentMethod: null, deliveryMethod: null },
        floatPanel: { visible: false, queryId: null, isEmpty: true, data: {} },
      });
    },
    setUserLanguage: (state, action) => {
      state.userConfig.language = action.payload;
    },
    setLanguage: (state, action) => {
      state.userConfig.literals = action.payload;
    },
    setUserRol: (state, action) => {
      state.userConfig.rol = action.payload;
    },
    setToken: (state, action) => {
      state.userConfig.token = action.payload;
      state.userConfig.logged = true;
    },
    setComponentActive: (state, action) => {
      state.userConfig.floatPanel.visible = false;
      state.userConfig.componentActive = action.payload;
    },
    updateDatatableConfiguration: (state, action) => {
      state.userConfig.datatable.configuration = action.payload;
    },
    updateDatatableFilter: (state, action) => {
      const { section, filter } = action.payload;
      state.userConfig.datatable.configuration.filterBy.section = section;
      state.userConfig.datatable.configuration.filterBy.filter = filter;
    },
    updateDatatableCheckColumn: (state, action) => {
      state.userConfig.datatable.checkColumn = typeof action.payload === "boolean" ? action.payload : !state.userConfig.datatable.checkColumn;
    },
    updateFloatPanelVisibility: (state) => {
      state.userConfig.floatPanel.visible = !state.userConfig.floatPanel.visible;
    },
    setFloatPanelComponent: (state, action) => {
      state.userConfig.floatPanel.component = action.payload;
    },
    setFloatPanelData: (state, action) => {
      state.userConfig.floatPanel.data = action.payload;
    },
    setOrder: (state, action) => {
      state.userConfig.order = action.payload;
    },
    setCartDiscountBusy: (state, action) => { state.userConfig.cartDiscountBusy = action.payload; },
    updateCartDiscounts: (state, action) => {
      if (state.userConfig.cart?.shoppingCartId === action.payload.shoppingCartId) state.userConfig.cart.Discounts = action.payload.discounts;
    },
    updateCart: (state, action) => {
      state.userConfig.cart = action.payload;
    },
    addProductCart: (state, action) => {
      Object.assign(state.userConfig.cart, freshIntent());
      const { productId, quantity, itemIds, variantId, note, Product } = action.payload;
      state.userConfig.cart.ShoppingCartProducts.push({
        id: Date.now(),
        itemIds,
        quantity,
        note,
        variantId,
        productId,
        Product,
      });
    },
    updateProductCart: (state, action) => {
      Object.assign(state.userConfig.cart, freshIntent());
      const { productId, itemIds, variantId, note, Product } = action.payload;

      const addedQuantity = Number.isSafeInteger(action.payload.quantityToAdd) && action.payload.quantityToAdd > 0 ? action.payload.quantityToAdd : 1;
      const index = findCartLineIndex(state.userConfig.cart.ShoppingCartProducts, action.payload);
      const item = index >= 0 ? state.userConfig.cart.ShoppingCartProducts[index] : null;

      if (item) {
        console.log("EXISTE");
        item.quantity = item.quantity + addedQuantity;
      } else {
        console.log("NO EXISTE");
        state.userConfig.cart.ShoppingCartProducts.push({
          id: Date.now(),
          itemIds,
          quantity: addedQuantity,
          note,
          variantId,
          productId,
          Product,
          key: generateCode(), // ¡Aquí generas un nuevo key si no existía!
        });
      }
    },
    removeProductCart: (state, action) => {
      Object.assign(state.userConfig.cart, freshIntent());
      const products = state.userConfig.cart.ShoppingCartProducts;
      const index = findCartLineIndex(products, action.payload);
      if (index < 0) return;
      if (products[index].quantity > 1 && !action.payload.removeAll) products[index].quantity -= 1;
      else products.splice(index, 1);
    },
    updateShippingAddresses: (state, action) => {
      state.userConfig.userData.addresses.shippingAddresses = action.payload;
    },
    updateBillingAddresses: (state, action) => {
      state.userConfig.userData.addresses.billingAddresses = action.payload;
    },
    setSearch: (state, action) => {
      state.userConfig.search = action.payload;
    },
    setPayment: (state, action) => {
      state.userConfig.payment = action.payload;
    },
  },
});

export const {
  updateHistoryOrder,
  updateChekoutAddress,
  updateChekoutPaymentMethod,
  updateChekoutDeliveryMethod,
  updateShopProducts,
  updateShopCategories,
  setCorporationData,
  setLoading,
  setUserLogged,
  setSessionReady,
  setLogoutPending,
  setUserData,
  setUserLanguage,
  setLanguage,
  setUserRol,
  setComponentActive,
  updateDatatableConfiguration,
  setToken,
  setUserLogOut,
  setAlert,
  removeAlert,
  updateDatatableFilter,
  updateDatatableCheckColumn,
  updateFloatPanelVisibility,
  setFloatPanelComponent,
  setFloatPanelData,
  setOrder,
  updateCart,
  updateCartDiscounts,
  setCartDiscountBusy,
  addProductCart,
  updateProductCart,
  removeProductCart,
  updateShippingAddresses,
  updateBillingAddresses,
  setSearch,
  setPayment,
} = userConfigSlice.actions;
export default userConfigSlice.reducer;
